import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Button, Upload, Slider, Select, Switch, Space,
  Typography, Divider, notification, Spin, Tooltip, Tag, Modal, Row, Col, Input
} from 'antd';
import {
  UploadOutlined, PlusOutlined, DeleteOutlined, SaveOutlined,
  EyeOutlined, DragOutlined, FontSizeOutlined, BoldOutlined, AlignCenterOutlined,
  AlignLeftOutlined, AlignRightOutlined, PictureOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Title, Text } = Typography;
const { Option } = Select;

const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });

const genId = () => Math.random().toString(36).slice(2, 9);

const DEFAULT_ROW = () => ({
  id: genId(),
  template: '',
  top_pct: 50,
  left_pct: 50,
  font_size: 28,
  color: '#000000',
  bold: false,
  text_anchor: 'middle',
  font_family: 'Noto Sans Devanagari',
});

const FONT_FAMILIES = [
  'Noto Sans Devanagari', 'Noto Serif Devanagari',
  'Hind', 'Mukta', 'Baloo 2', 'Eczar', 'Karma', 'Biryani',
  'Vesper Libre', 'Tillana', 'Rajdhani', 'Tiro Devanagari Hindi',
  'Rozha One', 'Yatra One', 'Kalam', 'Laila',
  'Poppins', 'Arial', 'Times New Roman', 'Georgia', 'Courier New',
];

const ANCHOR_ICONS = {
  start: <AlignLeftOutlined />,
  middle: <AlignCenterOutlined />,
  end: <AlignRightOutlined />,
};

export default function ReceiptTemplate() {
  const [config, setConfig]             = useState(null);
  const [rows, setRows]                 = useState([]);
  const [signConfig, setSignConfig]     = useState({ enabled: false, image_url: '', top_pct: 80, left_pct: 60, size_pct: 10 });
  const [templateImage, setTemplateImage] = useState('');
  const [isActive, setIsActive]         = useState(false);
  const [selectedId, setSelectedId]     = useState(null);
  const [tokens, setTokens]             = useState([]);
  const [saving, setSaving]             = useState(false);
  const [toggling, setToggling]         = useState(false);
  const [loading, setLoading]           = useState(true);
  const [previewUrl, setPreviewUrl]         = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [searchModal, setSearchModal]       = useState(false);
  const [searchText, setSearchText]         = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [searching, setSearching]           = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [uploadingSign, setUploadingSign] = useState(false);
  const [accounts, setAccounts]         = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null); // null = default (SN)
  const imgRef = useRef(null);
  const dragging = useRef(null);

  const applyConfig = (cfg) => {
    setConfig(cfg);
    setTemplateImage(cfg?.template_image || '');
    setIsActive(cfg?.is_active === 1 || cfg?.is_active === true);
    const tc = cfg?.text_config || {};
    setRows(Array.isArray(tc.rows) ? tc.rows : []);
    setSignConfig(tc.sign || { enabled: false, image_url: '', top_pct: 80, left_pct: 60, size_pct: 10 });
  };

  // ── Load accounts + tokens on mount, then load template for first account ──
  useEffect(() => {
    Promise.all([
      axios.get(`${baseUrl}/bank-account/active`, AUTH()),
      axios.get(`${baseUrl}/receipt-template/tokens`, AUTH()),
    ]).then(async ([acctRes, tokRes]) => {
      const accts = acctRes.data.status ? (acctRes.data.result || []) : [];
      setAccounts(accts);
      setTokens(tokRes.data.tokens || []);
      const firstId = accts[0]?.id || null;
      setSelectedAccount(firstId);
      const url = firstId
        ? `${baseUrl}/receipt-template/config?account_id=${firstId}`
        : `${baseUrl}/receipt-template/config`;
      const cfgRes = await axios.get(url, AUTH());
      applyConfig(cfgRes.data.result);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // ── Switch account → reload template for that account ────────────────────
  const handleAccountChange = async (accountId) => {
    setSelectedAccount(accountId);
    setLoading(true);
    try {
      const url = accountId
        ? `${baseUrl}/receipt-template/config?account_id=${accountId}`
        : `${baseUrl}/receipt-template/config`;
      const res = await axios.get(url, AUTH());
      applyConfig(res.data.result);
    } catch (e) {
      notification.error({ message: 'Failed to load template for selected account.' });
    } finally {
      setLoading(false);
    }
  };

  const selectedRow = rows.find(r => r.id === selectedId);

  // ── Update a field on the selected row ───────────────────────────────────
  const updateRow = (field, value) =>
    setRows(prev => prev.map(r => r.id === selectedId ? { ...r, [field]: value } : r));

  // ── Add new text field ────────────────────────────────────────────────────
  const addRow = (tokenKey = '') => {
    const r = { ...DEFAULT_ROW(), template: tokenKey };
    setRows(prev => [...prev, r]);
    setSelectedId(r.id);
  };

  // ── Delete row ────────────────────────────────────────────────────────────
  const deleteRow = (id) => {
    setRows(prev => prev.filter(r => r.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // ── Click on image to place selected token / new field ────────────────────
  const handleImageClick = useCallback((e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const leftPct = ((e.clientX - rect.left) / rect.width) * 100;
    const topPct  = ((e.clientY - rect.top)  / rect.height) * 100;
    if (selectedId) {
      setRows(prev => prev.map(r => r.id === selectedId
        ? { ...r, left_pct: +leftPct.toFixed(1), top_pct: +topPct.toFixed(1) } : r));
    }
  }, [selectedId]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const onMouseDown = (e, id) => {
    e.stopPropagation();
    setSelectedId(id);
        dragging.current = { id, startX: e.clientX, startY: e.clientY };
    const row = rows.find(r => r.id === id);
    dragging.current.startLeft = row.left_pct;
    dragging.current.startTop  = row.top_pct;
  };

  const onMouseMove = useCallback((e) => {
    if (!dragging.current || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragging.current.startX) / rect.width)  * 100;
    const dy = ((e.clientY - dragging.current.startY) / rect.height) * 100;
    const newLeft = Math.min(100, Math.max(0, dragging.current.startLeft + dx));
    const newTop  = Math.min(100, Math.max(0, dragging.current.startTop  + dy));
    setRows(prev => prev.map(r => r.id === dragging.current.id
      ? { ...r, left_pct: +newLeft.toFixed(1), top_pct: +newTop.toFixed(1) } : r));
  }, []);

  const onMouseUp = () => { dragging.current = null; };

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, [onMouseMove]);

  // ── Upload template image ─────────────────────────────────────────────────
  const handleTemplateUpload = async ({ file }) => {
    setUploadingTemplate(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${baseUrl}/receipt-template/upload-image`, fd,
        { headers: { ...AUTH().headers, 'Content-Type': 'multipart/form-data' } });
      if (res.data.status) { setTemplateImage(res.data.image_url); notification.success({ message: 'Template image uploaded!' }); }
      else notification.error({ message: res.data.message });
    } catch (e) { notification.error({ message: 'Upload failed.' }); }
    finally { setUploadingTemplate(false); }
  };

  // ── Upload sign image ─────────────────────────────────────────────────────
  const handleSignUpload = async ({ file }) => {
    setUploadingSign(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${baseUrl}/receipt-template/upload-image`, fd,
        { headers: { ...AUTH().headers, 'Content-Type': 'multipart/form-data' } });
      if (res.data.status) { setSignConfig(s => ({ ...s, image_url: res.data.image_url })); notification.success({ message: 'Sign image uploaded!' }); }
      else notification.error({ message: res.data.message });
    } catch (e) { notification.error({ message: 'Upload failed.' }); }
    finally { setUploadingSign(false); }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!templateImage) return notification.warning({ message: 'Please upload a template image first.' });
    setSaving(true);
    try {
      const res = await axios.post(`${baseUrl}/receipt-template/save`,
        { text_config: { rows, sign: signConfig }, template_image: templateImage,
          is_active: isActive ? 1 : 0, bank_account_id: selectedAccount || null }, AUTH());
      if (res.data.status) notification.success({ message: 'Template saved successfully!' });
      else notification.error({ message: res.data.message });
    } catch (e) { notification.error({ message: 'Save failed.' }); }
    finally { setSaving(false); }
  };

  // ── Toggle template active/inactive ──────────────────────────────────────
  const handleToggle = async (val) => {
    setToggling(true);
    try {
      await axios.post(`${baseUrl}/receipt-template/save`,
        { text_config: { rows, sign: signConfig }, template_image: templateImage,
          is_active: val ? 1 : 0, bank_account_id: selectedAccount || null },
        AUTH());
      setIsActive(val);
      notification.success({
        message: val ? 'Template Mode Enabled' : 'Template Mode Disabled',
        description: val ? 'Receipts will now use the image template.' : 'Receipts will now use the HTML layout.',
        placement: 'topRight', duration: 2,
      });
    } catch (e) {
      notification.error({ message: 'Toggle failed.' });
    } finally {
      setToggling(false);
    }
  };

  // ── Search payments for preview ───────────────────────────────────────────
  const handleSearch = async () => {
    if (!searchText.trim()) return;
    setSearching(true);
    const q = searchText.trim();
    // Determine if it looks like a phone number or receipt number
    const isPhone = /^\d{7,}$/.test(q.replace(/\s/g, ''));
    try {
      const params = { page: 1, limit: 15, payment_virify: 'verified', bank_account: selectedAccount || 1 };
      if (isPhone) params.phone = q;
      else params.receipt_no = q;

      const res = await axios.get(`${baseUrl}/payments/list`, { ...AUTH(), params });
      const list = res.data?.result || [];
      setSearchResults(list);
    } catch (e) {
      notification.error({ message: 'Search failed.' });
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPayment = async (payId) => {
    setSearchModal(false);
    setSearchText('');
    setSearchResults([]);
    setPreviewUrl('');       // clear old preview
    setPreviewVisible(true); // show modal with loading state
    try {
      // Fetch the receipt image with auth header — <img src> can't send auth headers
      const res = await axios.get(
        `${baseUrl}/receipt-template/generate/${payId}`,
        { ...AUTH(), responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(res.data);
      setPreviewUrl(blobUrl);
    } catch (e) {
      notification.error({ message: 'Could not generate preview. Check template and payment data.' });
      setPreviewVisible(false);
    }
  };

  const handlePreview = () => {
    setSearchModal(true);
  };

  if (loading) return <div style={{ padding: 40 }}><Spin tip="Loading..." /></div>;

  const labelFor = (tokenKey) => {
    const t = tokens.find(t => t.key === tokenKey);
    return t ? t.label : tokenKey;
  };

  return (
    <div style={{ padding: '16px 20px', background: '#f5f7fa', minHeight: '100vh' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Title level={4} style={{ margin: 0 }}>Receipt Template Designer</Title>
          <Select
            style={{ width: 220 }}
            placeholder="Select Account"
            value={selectedAccount}
            onChange={handleAccountChange}
            allowClear
            onClear={() => handleAccountChange(null)}
          >
            {accounts.map(a => (
              <Option key={a.id} value={a.id}>{a.account_name}</Option>
            ))}
          </Select>
        </Space>
        <Space size="middle">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            background: isActive ? '#f6ffed' : '#fff1f0',
            border: `1px solid ${isActive ? '#b7eb8f' : '#ffa39e'}`,
            borderRadius: 8, padding: '6px 14px' }}>
            <Switch
              checked={isActive}
              loading={toggling}
              onChange={handleToggle}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
            <Text style={{ fontWeight: 600, fontSize: 13, color: isActive ? '#52c41a' : '#ff4d4f' }}>
              {isActive ? 'Template Active' : 'Template Disabled (HTML mode)'}
            </Text>
          </div>
          <Button icon={<EyeOutlined />} onClick={handlePreview}>Preview</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save Template</Button>
        </Space>
      </div>

      <Row gutter={16}>
        {/* ── LEFT: Token Palette ── */}
        <Col span={selectedId ? 4 : 6}>
          {/* Template image upload */}
          <Card size="small" title="Template Image" style={{ marginBottom: 12 }}>
            <Upload showUploadList={false} customRequest={handleTemplateUpload} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploadingTemplate} block>
                {templateImage ? 'Replace Template' : 'Upload Template Image'}
              </Button>
            </Upload>
            {templateImage && (
              <img src={templateImage} alt="template thumb"
                style={{ width: '100%', marginTop: 8, borderRadius: 4, border: '1px solid #eee' }} />
            )}
          </Card>

          {/* Available tokens */}
          <Card size="small" title="Available Tokens" style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
              Click a token to add it as a new text field on the template
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {tokens.map(t => (
                <Tag key={t.key} color="blue" style={{ cursor: 'pointer', marginBottom: 4 }}
                  onClick={() => addRow(t.key)}>
                  {t.key}
                </Tag>
              ))}
            </div>
            <Button type="dashed" icon={<PlusOutlined />} block style={{ marginTop: 8 }}
              onClick={() => addRow('')}>
              Add Empty Field
            </Button>
          </Card>

          {/* Sign / Logo overlay */}
          <Card size="small" title={<span><PictureOutlined /> Sign / Logo Overlay</span>} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Switch checked={signConfig.enabled} onChange={v => setSignConfig(s => ({ ...s, enabled: v }))} />
              <Text>Enable sign image</Text>
            </div>
            {signConfig.enabled && (
              <>
                <Upload showUploadList={false} customRequest={handleSignUpload} accept="image/*">
                  <Button icon={<UploadOutlined />} loading={uploadingSign} size="small" block style={{ marginBottom: 8 }}>
                    {signConfig.image_url ? 'Replace Sign' : 'Upload Sign Image'}
                  </Button>
                </Upload>
                {signConfig.image_url && (
                  <img src={signConfig.image_url} alt="sign"
                    style={{ maxHeight: 50, marginBottom: 8, border: '1px solid #eee', borderRadius: 4 }} />
                )}
                <div style={{ fontSize: 12, marginBottom: 4 }}>Top % <Slider min={0} max={100} step={0.5} value={signConfig.top_pct} onChange={v => setSignConfig(s => ({ ...s, top_pct: v }))} /></div>
                <div style={{ fontSize: 12, marginBottom: 4 }}>Left % <Slider min={0} max={100} step={0.5} value={signConfig.left_pct} onChange={v => setSignConfig(s => ({ ...s, left_pct: v }))} /></div>
                <div style={{ fontSize: 12 }}>Size % <Slider min={2} max={40} step={1} value={signConfig.size_pct} onChange={v => setSignConfig(s => ({ ...s, size_pct: v }))} /></div>
              </>
            )}
          </Card>

          {selectedId && (
            <div style={{ marginTop: 4, padding: '6px 10px', background: '#e6f7ff',
              borderRadius: 6, fontSize: 11, color: '#1890ff', textAlign: 'center' }}>
              ✏️ Editing field — see right panel →
            </div>
          )}
        </Col>

        {/* ── CENTER: Visual Editor ── */}
        <Col span={selectedId ? 13 : 18}>
          <Card size="small" title="Template Editor — Click image to move selected field, drag field labels to reposition"
            bodyStyle={{ padding: 0 }}>
            {!templateImage ? (
              <div style={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#fafafa', flexDirection: 'column', gap: 12 }}>
                <PictureOutlined style={{ fontSize: 48, color: '#bbb' }} />
                <Text type="secondary">Upload a template image to start designing</Text>
              </div>
            ) : (
              <div style={{ position: 'relative', display: 'inline-block', width: '100%', userSelect: 'none' }}
                onClick={handleImageClick}>
                <img ref={imgRef} src={templateImage} alt="template"
                  style={{ width: '100%', display: 'block', pointerEvents: 'none' }} />

                {/* Text field overlays */}
                {rows.map(row => {
                  const isSelected = row.id === selectedId;
                  const label = row.template || '(empty)';
                  return (
                    <div key={row.id}
                      onMouseDown={e => onMouseDown(e, row.id)}
                      style={{
                        position: 'absolute',
                        top: `${row.top_pct}%`,
                        left: `${row.left_pct}%`,
                        transform: row.text_anchor === 'middle' ? 'translate(-50%, -50%)'
                          : row.text_anchor === 'end' ? 'translate(-100%, -50%)' : 'translate(0, -50%)',
                        cursor: 'grab',
                        background: isSelected ? 'rgba(24,144,255,0.15)' : 'rgba(255,255,255,0.7)',
                        border: isSelected ? '2px dashed #1890ff' : '1px dashed #aaa',
                        borderRadius: 3,
                        padding: '2px 6px',
                        fontSize: `${Math.min(row.font_size * 0.35, 14)}px`,
                        fontWeight: row.bold ? 700 : 400,
                        color: row.color,
                        whiteSpace: 'nowrap',
                        zIndex: isSelected ? 10 : 5,
                        boxShadow: isSelected ? '0 0 0 2px rgba(24,144,255,0.3)' : 'none',
                      }}>
                      <DragOutlined style={{ marginRight: 3, opacity: 0.5, fontSize: 10 }} />
                      {label.length > 20 ? label.slice(0, 20) + '…' : label}
                      <DeleteOutlined
                        style={{ marginLeft: 6, fontSize: 10, color: '#ff4d4f', cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); deleteRow(row.id); }} />
                    </div>
                  );
                })}

                {/* Sign overlay indicator */}
                {signConfig.enabled && signConfig.image_url && (
                  <div style={{
                    position: 'absolute',
                    top: `${signConfig.top_pct}%`,
                    left: `${signConfig.left_pct}%`,
                    transform: 'translate(-50%, -50%)',
                    border: '2px dashed #52c41a',
                    borderRadius: 4,
                    padding: '4px 8px',
                    background: 'rgba(255,255,255,0.8)',
                    fontSize: 11,
                    color: '#52c41a',
                    pointerEvents: 'none',
                  }}>
                    ✍ Sign/Logo
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Field list table */}
          <Card size="small" title="All Fields" style={{ marginTop: 12 }}>
            {rows.length === 0 ? (
              <Text type="secondary">No fields added yet. Click a token from the left panel.</Text>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {rows.map(row => (
                  <Tag key={row.id}
                    style={{
                      cursor: 'pointer',
                      border: selectedId === row.id ? '1.5px solid #1890ff' : '1px solid #d9d9d9',
                      background: selectedId === row.id ? '#e6f7ff' : '#fff',
                      padding: '4px 10px',
                    }}
                    onClick={() => setSelectedId(row.id)}
                    closable onClose={() => deleteRow(row.id)}>
                    <FontSizeOutlined style={{ marginRight: 4 }} />
                    {row.template || '(empty)'}
                    <span style={{ marginLeft: 6, fontSize: 10, color: '#888' }}>
                      {row.top_pct}% / {row.left_pct}%
                    </span>
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* ── RIGHT: Inline Edit Panel (shows when a field is selected) ── */}
        {selectedRow && (
          <Col span={7}>
            <Card size="small"
              title={<span style={{ fontSize: 13 }}>✏️ {labelFor(selectedRow.template) || 'Edit Field'}</span>}
              extra={
                <Button danger icon={<DeleteOutlined />} size="small"
                  onClick={() => { deleteRow(selectedId); }}>
                  Delete
                </Button>
              }
              style={{ position: 'sticky', top: 16, maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>

              <div style={{ marginBottom: 12 }}>
                <Text style={{ fontSize: 11, fontWeight: 600 }}>Token / Text</Text>
                <Input value={selectedRow.template}
                  onChange={e => updateRow('template', e.target.value)}
                  placeholder="{name} or fixed text"
                  style={{ marginTop: 4 }} size="small" />
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {tokens.map(t => (
                    <Tag key={t.key} color="blue" style={{ cursor: 'pointer', fontSize: 10, margin: '2px 0' }}
                      onClick={() => updateRow('template', t.key)}>
                      {t.key}
                    </Tag>
                  ))}
                </div>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 11 }}>Font Size: <b>{selectedRow.font_size}px</b></Text>
                <Slider min={8} max={120} value={selectedRow.font_size}
                  onChange={v => updateRow('font_size', v)} />
              </div>

              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 11 }}>Top %: <b>{selectedRow.top_pct}</b></Text>
                <Slider min={0} max={100} step={0.5} value={selectedRow.top_pct}
                  onChange={v => updateRow('top_pct', v)} />
              </div>

              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 11 }}>Left %: <b>{selectedRow.left_pct}</b></Text>
                <Slider min={0} max={100} step={0.5} value={selectedRow.left_pct}
                  onChange={v => updateRow('left_pct', v)} />
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 11 }}>Color</Text><br />
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <input type="color" value={selectedRow.color}
                    onChange={e => updateRow('color', e.target.value)}
                    style={{ width: 36, height: 28, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer', padding: 1 }} />
                  <Input size="small" value={selectedRow.color}
                    onChange={e => updateRow('color', e.target.value)}
                    style={{ width: 80 }} maxLength={7} />
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 11 }}>Font Family</Text>
                <Select size="small" value={selectedRow.font_family}
                  onChange={v => updateRow('font_family', v)} style={{ width: '100%', marginTop: 4 }}>
                  {FONT_FAMILIES.map(f => <Option key={f} value={f}>{f}</Option>)}
                </Select>
              </div>

              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontSize: 11 }}>Alignment</Text>
                <Select size="small" value={selectedRow.text_anchor}
                  onChange={v => updateRow('text_anchor', v)} style={{ width: '100%', marginTop: 4 }}>
                  {Object.entries(ANCHOR_ICONS).map(([k, icon]) => (
                    <Option key={k} value={k}>{icon} {k}</Option>
                  ))}
                </Select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Switch size="small" checked={selectedRow.bold} onChange={v => updateRow('bold', v)} />
                <Text style={{ fontSize: 12 }}><BoldOutlined /> Bold</Text>
              </div>

              <Button type="default" size="small" block
                onClick={() => setSelectedId(null)}>
                ✕ Close
              </Button>
            </Card>
          </Col>
        )}
      </Row>

      {/* ── Payment Search Modal (for preview) ── */}
      <Modal
        open={searchModal}
        title="Select a Payment to Preview"
        onCancel={() => { setSearchModal(false); setSearchText(''); setSearchResults([]); }}
        footer={null}
        width={560}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Input
            placeholder="Search by phone number or receipt no..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
          <Button type="primary" loading={searching} onClick={handleSearch}>Search</Button>
        </div>

        {searchResults.length === 0 && !searching && searchText && (
          <Text type="secondary">No verified payments found. Try a different phone or receipt number.</Text>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {searchResults.map(p => (
            <div key={p.payment_id}
              onClick={() => handleSelectPayment(p.payment_id)}
              style={{
                padding: '10px 14px', border: '1px solid #e8edf4', borderRadius: 6,
                cursor: 'pointer', background: '#fafafa',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#e6f7ff'}
              onMouseLeave={e => e.currentTarget.style.background = '#fafafa'}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name || p.name_eng || '—'}</div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  📞 {p.phone_no} &nbsp;|&nbsp; Receipt: {p.receipt_no || '—'} &nbsp;|&nbsp; ₹{p.amount}
                </div>
              </div>
              <Button size="small" type="primary">Preview</Button>
            </div>
          ))}
        </div>
      </Modal>

      {/* ── Preview Modal ── */}
      <Modal open={previewVisible}
        onCancel={() => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl('');
          setPreviewVisible(false);
        }}
        footer={null} width={900} title="Receipt Preview">
        {previewVisible && !previewUrl && (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="Generating receipt..." /></div>
        )}
        {previewUrl && (
          <img src={previewUrl} alt="receipt preview" style={{ width: '100%' }} />
        )}
      </Modal>
    </div>
  );
}
